import { ImageViewer } from 'antd-mobile';
import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ampli } from '../../../../ampli';
import { ImageCounterCenterBottom } from '../../../../components/common/counter/ImageCounterCenterBottom';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusType } from '../../../../constants/product/product.constant';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IProductImage } from '../../../../interfaces/Product.interfaces';
import { ProductImageOverlay } from './ProductImageOverlay';

interface ProductImageSectionProps {
  images: IProductImage[];
  status: ProductStatusType;
  pageType: 'odp' | 'pdp' | 'new-product';
  productTitle: string;
  productCode: string;
  onProductImageClick?: () => void;
  disableLoginPrompt?: boolean;
}

export const ProductImageSection = ({
  images,
  status,
  pageType,
  productTitle,
  productCode,
  onProductImageClick,
  disableLoginPrompt = false,
}: ProductImageSectionProps) => {
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageViewerIndex, setCurrentImageViewerIndex] = useState(0);
  const [previousImageViewerIndex, setPreviousImageViewerIndex] = useState(0);
  const imageViewerRef = useRef<{ swipeTo: (index: number) => void } | null>(null);
  const shouldCloseRef = useRef(false);
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  useEffect(() => {
    if (!imageViewerVisible) {
      return undefined;
    }

    const cleanupFunctions: (() => void)[] = [];
    const touchStartInfo = new Map<Element, { x: number; y: number; time: number }>();
    const registeredElements = new Set<Element>();

    const setupEventListeners = () => {
      // ImageViewer의 여러 요소에 클릭/터치 방지 이벤트를 추가
      const selectors = [
        '#root-modal .adm-image-viewer',
        '#root-modal .adm-image-viewer-mask',
        '#root-modal .adm-image-viewer-content',
        '#root-modal .adm-image-viewer-slides',
        '#root-modal .adm-image-viewer-slide',
        '#root-modal .adm-image-viewer-image-wrapper',
        '#root-modal .adm-image-viewer-image',
      ];

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          // 이미 등록된 요소는 건너뛰기
          if (registeredElements.has(element)) {
            return;
          }
          registeredElements.add(element);
          const handleClick = (e: Event) => {
            const target = e.target as HTMLElement;
            // 버튼이 아닌 경우 클릭 이벤트 전파를 막습니다
            if (!target.closest('button') && !target.closest('[role="button"]')) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            }
          };

          const handleTouchStart = (e: Event) => {
            const touchEvent = e as TouchEvent;
            if (touchEvent.touches.length === 1) {
              const touch = touchEvent.touches[0];
              touchStartInfo.set(element, {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
              });
            } else {
              // 멀티터치(핀치줌)의 경우 정보 제거
              touchStartInfo.delete(element);
            }
          };

          const handleTouchEnd = (e: Event) => {
            const touchEvent = e as TouchEvent;
            const target = touchEvent.target as HTMLElement;
            if (target.closest('button') || target.closest('[role="button"]')) {
              touchStartInfo.delete(element);
              return;
            }

            const startInfo = touchStartInfo.get(element);
            if (startInfo && touchEvent.changedTouches.length === 1) {
              const touch = touchEvent.changedTouches[0];
              const endX = touch.clientX;
              const endY = touch.clientY;
              const endTime = Date.now();

              // 이동 거리 계산
              const deltaX = Math.abs(endX - startInfo.x);
              const deltaY = Math.abs(endY - startInfo.y);
              const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

              // 시간 차이 계산
              const duration = endTime - startInfo.time;

              // 짧은 시간(300ms 이하)이고 이동 거리가 적으면(10px 이하) 탭으로 간주
              if (duration < 300 && distance < 10) {
                touchEvent.preventDefault();
                touchEvent.stopPropagation();
                touchEvent.stopImmediatePropagation();
              }
            }

            touchStartInfo.delete(element);
          };

          // 여러 번 이벤트를 등록하여 더 확실하게 막기
          element.addEventListener('click', handleClick, true);
          element.addEventListener('click', handleClick, false);
          element.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
          element.addEventListener('touchend', handleTouchEnd, { capture: true, passive: false });
          element.addEventListener('touchend', handleTouchEnd, { capture: false, passive: false });

          cleanupFunctions.push(() => {
            element.removeEventListener('click', handleClick, true);
            element.removeEventListener('click', handleClick, false);
            element.removeEventListener('touchstart', handleTouchStart, true);
            element.removeEventListener('touchend', handleTouchEnd, true);
            element.removeEventListener('touchend', handleTouchEnd, false);
          });
        });
      });
    };

    // 즉시 실행하고, 여러 번 재시도하여 DOM이 준비되었을 때 확실하게 적용
    setupEventListeners();
    const timer1 = setTimeout(setupEventListeners, 0);
    const timer2 = setTimeout(setupEventListeners, 50);
    const timer3 = setTimeout(setupEventListeners, 100);
    const timer4 = setTimeout(setupEventListeners, 200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      cleanupFunctions.forEach((cleanup) => cleanup());
      touchStartInfo.clear();
    };
  }, [imageViewerVisible]);

  const handleProductImageClick = () => {
    if (pageType === 'new-product') {
      onProductImageClick?.();
    } else {
      ReactGA.event('pvm_click_pdp_product_photo', {
        action: 'click',
        category: 'product_image_view',
        user_type: isUserLoggedIn ? 'member' : 'nonmember',
        page_type: pageType,
        product_title: productTitle,
      });
      ampli.pvmClickPdpProductPhoto({
        user_type: isUserLoggedIn ? 'member' : 'nonmember',
        page_type: pageType,
        product_title: productTitle,
        product_code: productCode,
      });
    }

    if (!isUserLoggedIn && pageType === 'odp' && !disableLoginPrompt) {
      ReactGA.event('pvm_click_login_button', {
        isModal: false,
      });
    }

    if (!isUserLoggedIn && !disableLoginPrompt) {
      navigate(pageInfo.loginPage.path);
    }
  };

  const handleImageViewerOpen = (index: number) => {
    setCurrentImageViewerIndex(index);
    setPreviousImageViewerIndex(index);
    setImageViewerVisible(true);
    shouldCloseRef.current = false;
    handleProductImageClick();
  };

  const handleImageViewerClose = () => {
    // shouldCloseRef가 true일 때만 실제로 닫습니다
    if (shouldCloseRef.current) {
      setImageViewerVisible(false);
      shouldCloseRef.current = false;
    }
  };

  const handleCloseButtonClick = () => {
    shouldCloseRef.current = true;
    setImageViewerVisible(false);
  };

  const handlePrevImage = () => {
    if (currentImageViewerIndex > 0) {
      const newIndex = currentImageViewerIndex - 1;
      setCurrentImageViewerIndex(newIndex);
      imageViewerRef.current?.swipeTo(newIndex);
    }
  };

  const handleNextImage = () => {
    if (currentImageViewerIndex < filteredImages.length - 1) {
      const newIndex = currentImageViewerIndex + 1;
      setCurrentImageViewerIndex(newIndex);
      imageViewerRef.current?.swipeTo(newIndex);
    }
  };

  const filteredImages = images
    .sort((a, b) => {
      if (!a.order || !b.order) {
        return 0;
      }
      return a.order - b.order;
    })
    .filter((image) => image.type !== 'LIST');

  const imageViewerImages = filteredImages.map((image) => image.imageUrl);

  if (images && images.length > 0) {
    return (
      <>
        <SwiperWrapper id={'prod-pictures'}>
          <Swiper
            slidesPerView={1}
            onSlideChange={(slide) => {
              ampli.pvmSwipeProductPhoto({
                user_type: isUserLoggedIn ? 'member' : 'nonmember',
                page_type: pageType,
                product_title: productTitle,
                product_code: productCode,
                direction: slide.swipeDirection,
                image_number: `${slide.previousIndex}->${slide.realIndex}`,
                mode: 'in_page',
              });
              setActiveSlideImageIndex(slide.realIndex);
            }}
            edgeSwipeDetection
            loop
          >
            {filteredImages.map((slideImage, slideImageIndex) => {
              return (
                <CustomSwiperSlide key={slideImage.id}>
                  <ProductImageOverlay
                    onClick={() => handleImageViewerOpen(activeSlideImageIndex)}
                    disableLoginPrompt={disableLoginPrompt}
                  />
                  <ProductImage
                    src={slideImage.imageUrl}
                    alt={productTitle}
                    onClick={() => handleImageViewerOpen(activeSlideImageIndex)}
                  />
                  <div className={'swiper-lazy-preloader'} />
                  {status === 'SOLDOUT' && <Dimmed />}
                </CustomSwiperSlide>
              );
            })}
          </Swiper>

          {filteredImages.length > 0 && (
            <ImageCounterCenterBottom currentPageNum={activeSlideImageIndex + 1} totalPageNum={filteredImages.length} />
          )}
          {status === 'SOLDOUT' && (
            <>
              <DimmedCenterMessage1>{'SOLD'}</DimmedCenterMessage1>
              <DimmedCenterMessage2>{'OUT'}</DimmedCenterMessage2>
            </>
          )}
        </SwiperWrapper>

        {imageViewerVisible && (
          <ImageViewer.Multi
            ref={imageViewerRef}
            visible={imageViewerVisible}
            images={imageViewerImages}
            defaultIndex={currentImageViewerIndex}
            onClose={handleImageViewerClose}
            onIndexChange={(index) => {
              const direction = index > previousImageViewerIndex ? 'next' : 'prev';
              ampli.pvmSwipeProductPhoto({
                user_type: isUserLoggedIn ? 'member' : 'nonmember',
                page_type: pageType,
                product_title: productTitle,
                product_code: productCode,
                direction,
                image_number: `${previousImageViewerIndex}->${index}`,
                mode: 'full_screen',
              });
              setPreviousImageViewerIndex(index);
              setCurrentImageViewerIndex(index);
            }}
            getContainer={() => document.getElementById('root-modal') || document.body}
            renderFooter={(image, index) => (
              <ImageViewerControls>
                <ImageViewerTopBar>
                  <CloseButton onClick={handleCloseButtonClick}>{'✕'}</CloseButton>
                </ImageViewerTopBar>
                <NavButtonLeft onClick={handlePrevImage} disabled={index === 0}>
                  <svg
                    width={'24'}
                    height={'24'}
                    viewBox={'0 0 24 24'}
                    fill={'none'}
                    xmlns={'http://www.w3.org/2000/svg'}
                  >
                    <path
                      d={'M15 18L9 12L15 6'}
                      stroke={'currentColor'}
                      strokeWidth={'2'}
                      strokeLinecap={'round'}
                      strokeLinejoin={'round'}
                    />
                  </svg>
                </NavButtonLeft>
                <NavButtonRight onClick={handleNextImage} disabled={index === imageViewerImages.length - 1}>
                  <svg
                    width={'24'}
                    height={'24'}
                    viewBox={'0 0 24 24'}
                    fill={'none'}
                    xmlns={'http://www.w3.org/2000/svg'}
                  >
                    <path
                      d={'M9 18L15 12L9 6'}
                      stroke={'currentColor'}
                      strokeWidth={'2'}
                      strokeLinecap={'round'}
                      strokeLinejoin={'round'}
                    />
                  </svg>
                </NavButtonRight>
                <ImageViewerBottomBar>
                  <ImageCounter>{`${index + 1} / ${imageViewerImages.length}`}</ImageCounter>
                </ImageViewerBottomBar>
              </ImageViewerControls>
            )}
          />
        )}
      </>
    );
  }

  return null;
};

const SwiperWrapper = styled.div`
  position: relative;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  position: relative;

  cursor: pointer;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Dimmed = styled.div`
  position: absolute;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  border-radius: var(--radius-medium, 8px);
  background: rgba(35, 36, 41, 0.6);
  backdrop-filter: blur(4px);
`;

const DimmedCenterMessage1 = styled.span`
  position: absolute;
  left: 0px;
  top: calc(42% - 15px);

  font-weight: 700;
  font-size: 25px;

  width: 100%;
  text-align: center;

  z-index: 50;

  color: #ffffff;
`;

const DimmedCenterMessage2 = styled.span`
  position: absolute;
  left: 0px;
  top: calc(42% + 15px);

  font-weight: 700;
  font-size: 25px;

  width: 100%;
  text-align: center;

  z-index: 50;

  color: #ffffff;
`;

const ImageViewerControls = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1000;

  @media screen and (min-width: 512px) {
    max-width: 100vw;
  }
`;

const ImageViewerTopBar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px;
  pointer-events: auto;
`;

const CloseButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:active {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const ImageViewerBottomBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  pointer-events: auto;
`;

const ImageCounter = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 16px;
  border-radius: 20px;
`;

const NavButtonLeft = styled.button<{ disabled?: boolean }>`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  pointer-events: auto;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)')};
  }

  &:active {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.9)')};
  }

  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const NavButtonRight = styled.button<{ disabled?: boolean }>`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  pointer-events: auto;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)')};
  }

  &:active {
    background: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.9)')};
  }

  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
  `}
`;
